import { useState, useEffect } from "react";
import { API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { 
  Tag, 
  Plus, 
  Trash2, 
  Percent,
  Calendar,
  Users,
  Gift
} from "lucide-react";

const AdminPromoCodes = () => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  
  const [form, setForm] = useState({
    code: "",
    discount_percent: 10,
    max_uses: "",
    valid_until: "",
    course_id: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [promoRes, coursesRes] = await Promise.all([
        axios.get(`${API}/promo-codes`, { withCredentials: true }),
        axios.get(`${API}/courses`, { withCredentials: true })
      ]);
      setPromoCodes(promoRes.data);
      setCourses(coursesRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.code || !form.discount_percent) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      await axios.post(`${API}/promo-codes`, {
        code: form.code.toUpperCase(),
        discount_percent: parseInt(form.discount_percent),
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        valid_until: form.valid_until ? new Date(form.valid_until).toISOString() : null,
        course_id: form.course_id || null
      }, { withCredentials: true });
      
      toast.success("Promo code created!");
      setIsCreateOpen(false);
      setForm({ code: "", discount_percent: 10, max_uses: "", valid_until: "", course_id: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create promo code");
    }
  };

  const handleToggle = async (promoId) => {
    try {
      const response = await axios.put(`${API}/promo-codes/${promoId}/toggle`, {}, { withCredentials: true });
      setPromoCodes(prev => prev.map(p => 
        p.promo_id === promoId ? { ...p, is_active: response.data.is_active } : p
      ));
      toast.success("Promo code updated");
    } catch (error) {
      toast.error("Failed to update promo code");
    }
  };

  const handleDelete = async (promoId) => {
    if (!window.confirm("Are you sure you want to delete this promo code?")) return;

    try {
      await axios.delete(`${API}/promo-codes/${promoId}`, { withCredentials: true });
      toast.success("Promo code deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete promo code");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "No expiry";
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-700"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in" data-testid="admin-promo-codes">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-stone-900" style={{ fontFamily: 'Outfit' }}>
              Promo Codes
            </h1>
            <p className="text-stone-600 mt-1">Create and manage discount codes</p>
          </div>
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-violet-700 hover:bg-violet-800 rounded-full" data-testid="create-promo-btn">
                <Plus className="h-4 w-4 mr-2" />
                Create Promo Code
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Outfit' }}>Create Promo Code</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div>
                  <Label>Code *</Label>
                  <Input
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    placeholder="e.g., SUMMER20"
                    className="uppercase"
                    data-testid="promo-code-input"
                  />
                </div>
                <div>
                  <Label>Discount Percentage *</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={form.discount_percent}
                      onChange={(e) => setForm({ ...form, discount_percent: e.target.value })}
                      data-testid="discount-input"
                    />
                    <span className="text-stone-500">%</span>
                  </div>
                </div>
                <div>
                  <Label>Max Uses (leave empty for unlimited)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={form.max_uses}
                    onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                    placeholder="Unlimited"
                    data-testid="max-uses-input"
                  />
                </div>
                <div>
                  <Label>Valid Until (optional)</Label>
                  <Input
                    type="datetime-local"
                    value={form.valid_until}
                    onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                    data-testid="valid-until-input"
                  />
                </div>
                <div>
                  <Label>Apply to Course (optional)</Label>
                  <Select 
                    value={form.course_id} 
                    onValueChange={(v) => setForm({ ...form, course_id: v === "all" ? "" : v })}
                  >
                    <SelectTrigger data-testid="course-select">
                      <SelectValue placeholder="All Courses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Courses</SelectItem>
                      {courses.map(course => (
                        <SelectItem key={course.course_id} value={course.course_id}>
                          {course.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  className="w-full bg-violet-700 hover:bg-violet-800"
                  onClick={handleCreate}
                  data-testid="submit-promo-btn"
                >
                  Create Promo Code
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-white rounded-xl border border-stone-100">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center">
                <Tag className="h-6 w-6 text-violet-700" />
              </div>
              <div>
                <p className="text-sm text-stone-500">Total Codes</p>
                <p className="text-2xl font-bold text-stone-900">{promoCodes.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white rounded-xl border border-stone-100">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Gift className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-stone-500">Active Codes</p>
                <p className="text-2xl font-bold text-stone-900">
                  {promoCodes.filter(p => p.is_active).length}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white rounded-xl border border-stone-100">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <Users className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-stone-500">Total Uses</p>
                <p className="text-2xl font-bold text-stone-900">
                  {promoCodes.reduce((acc, p) => acc + (p.times_used || 0), 0)}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Promo Codes List */}
        <Card className="bg-white rounded-xl border border-stone-100">
          <CardContent className="p-0">
            {promoCodes.length === 0 ? (
              <div className="p-12 text-center">
                <Tag className="h-16 w-16 text-stone-300 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-stone-900 mb-2">No promo codes yet</h3>
                <p className="text-stone-500">Create your first promo code to offer discounts</p>
              </div>
            ) : (
              <div className="divide-y divide-stone-100">
                {promoCodes.map((promo) => (
                  <div 
                    key={promo.promo_id}
                    className="p-4 flex items-center gap-4 hover:bg-stone-50 transition-colors"
                    data-testid={`promo-row-${promo.promo_id}`}
                  >
                    <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Percent className="h-6 w-6 text-violet-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-semibold text-stone-900">
                          {promo.code}
                        </span>
                        <Badge className={promo.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-100 text-stone-500'}>
                          {promo.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-stone-500 mt-1">
                        <span>{promo.discount_percent}% off</span>
                        <span>•</span>
                        <span>{promo.times_used || 0} uses</span>
                        {promo.max_uses && (
                          <>
                            <span>•</span>
                            <span>Max: {promo.max_uses}</span>
                          </>
                        )}
                        <span>•</span>
                        <span>{formatDate(promo.valid_until)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={promo.is_active}
                        onCheckedChange={() => handleToggle(promo.promo_id)}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDelete(promo.promo_id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AdminPromoCodes;
